import { Box, IconButton, Link, useDisclosure } from '@chakra-ui/react'
import React, { useMemo, useState } from 'react'
import { Table, TableColumn, TableRecord } from '@apex/component-library'
import { isNil, startCase } from 'lodash'

import { ALLOWED_POSITION_CANCEL_STATES } from '../../constants'
import { AccountDetailResponsePayload } from '@Types'
import { CancelPositionJournalTransferModal } from '../../components/CancelTransferModal/CancelPositionJournalModal'
import { DateTime } from 'luxon'
import { IoClose } from 'react-icons/io5'
import { PositionJournalTransfer } from '../../api/useGetPositionJournals'
import { TerminalPositionJournalStates } from '../../constants/cashTransferConstants'
import { removeUnderscores } from '../../utils'

const handleCanCancelTransfer = (state: TerminalPositionJournalStates) => {
  return ALLOWED_POSITION_CANCEL_STATES.has(state)
}

const buildTransferType = (transfer: PositionJournalTransfer, accountNumber: string) => {
  const method = removeUnderscores(transfer?.method) || ''
  let direction

  if (accountNumber === transfer?.positionJournalDetails?.sourceAccount) {
    direction = 'Debit'
  } else {
    direction = 'Credit'
  }

  return `${method} ${direction}`
}

const columns: TableColumn[] = [
  {
    key: 'date',
    label: 'Date',
    recordField: 'date',
    isSortable: false
  },
  {
    key: 'type',
    label: 'Transfer Type',
    recordField: 'type',
    isSortable: false
  },
  {
    key: 'security',
    label: 'Security',
    recordField: 'security',
    isSortable: false
  },
  {
    key: 'quantity',
    label: 'Quantity',
    recordField: 'quantity',
    isSortable: false,
    alignment: 'right'
  },
  {
    key: 'status',
    label: 'Status',
    recordField: 'status',
    isSortable: false
  },
  {
    key: 'cancel',
    label: 'Cancel',
    recordField: 'cancel',
    isSortable: false,
    alignment: 'right',
    cellRenderer: ({ value }: { value: JSX.Element }) => value
  }
]

type SelectedTransfer = { transfer: PositionJournalTransfer; type: string }

export interface Props {
  positionJournals: PositionJournalTransfer[]
  accountNumber: string
  accountDetails?: AccountDetailResponsePayload | null
  hasPositionCancelPermission: boolean
}

const PositionsTable = ({ positionJournals, accountNumber, accountDetails, hasPositionCancelPermission }: Props) => {
  const [selectedTransfer, setSelectedTransfer] = useState<SelectedTransfer>()

  const { isOpen, onOpen, onClose } = useDisclosure()

  const searchResults = positionJournals || []

  const tableData: TableRecord[] = useMemo(() => {
    const tableRowData: TableRecord[] = []
    for (let i = searchResults.length - 1; i >= 0; i--) {
      const transfer = searchResults[i]
      const type = buildTransferType(transfer, accountNumber)

      const date = transfer?.initiationTime
        ? DateTime.fromISO(transfer?.initiationTime).toLocaleString(DateTime.DATE_SHORT)
        : ''

      const security = isNil(transfer.positionJournalDetails?.securityId)
        ? ''
        : transfer.positionJournalDetails?.securityId!

      const quantity = isNil(transfer.positionJournalDetails?.quantity)
        ? ''
        : transfer.positionJournalDetails?.quantity!

      const openModal = (transferData: SelectedTransfer) => {
        setSelectedTransfer(transferData)
        onOpen()
      }

      const canCancel =
        !isNil(transfer?.method) &&
        !isNil(transfer?.status) &&
        !isNil(accountNumber) &&
        handleCanCancelTransfer(transfer.status)

      const cancelElement = canCancel ? (
        hasPositionCancelPermission ? (
          ((
            <IconButton
              variant='ghost'
              fontSize='20px'
              h='15px'
              minW='none'
              border='none'
              size='md'
              color='red'
              aria-label='Cancel Transfer'
              icon={<IoClose />}
              onClick={() => openModal({ transfer, type })}
            />
          ) as any)
        ) : null
      ) : (
        <Box as='span'>N/A</Box>
      )
      const statusLink = (
        <Link href={`/cashiering/details/${transfer.id}`} color='blue.400' target='_blank'>
          {startCase(transfer?.status)}
        </Link>
      ) as any
      tableRowData.push({
        date,
        type,
        security,
        quantity,
        key: i,
        status: statusLink,
        cancel: cancelElement
      })
    }
    return tableRowData
  }, [accountNumber, searchResults, onOpen, hasPositionCancelPermission])

  return (
    <>
      <Table columns={columns} data={tableData} size='md'></Table>
      <CancelPositionJournalTransferModal
        accountDetails={accountDetails}
        accountNumber={accountNumber}
        transferData={selectedTransfer?.transfer}
        type={selectedTransfer?.type || ''}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  )
}

export default PositionsTable
